import * as Schema from "effect/Schema";

/** Operations describe work, not file extensions or exclusive model families. */
export const MediaOperation = Schema.Literals([
  "image.generate",
  "image.edit",
  "video.generate",
  "video.animate",
  "audio.transcribe",
  "audio.speak",
  "audio.generate",
  "audio.separate",
  "audio.dub",
  "media.assemble",
]);
export type MediaOperation = typeof MediaOperation.Type;
const Key = Schema.String.check(Schema.isPattern(/^[a-zA-Z0-9][a-zA-Z0-9_-]{0,95}$/));
const Path = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(4096));
const Dimension = Schema.Int.check(
  Schema.isBetween({ minimum: 256, maximum: 1536 }),
  Schema.isMultipleOf(64),
);
export const CreateImageJob = Schema.Struct({
  requestKey: Key,
  scope: Schema.Struct({ kind: Schema.Literal("local-draft") }),
  operation: Schema.Literal("image.generate"),
  modelKey: Key,
  executionPolicy: Schema.Literal("local-only"),
  prompt: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(12000)),
  parameters: Schema.Struct({
    width: Dimension,
    height: Dimension,
    seed: Schema.Int.check(Schema.isBetween({ minimum: 0, maximum: 2147483647 })),
    quantity: Schema.Literal(1),
  }),
});
export type CreateImageJob = typeof CreateImageJob.Type;
export const CreateImageEditJob = Schema.Struct({
  ...CreateImageJob.fields,
  operation: Schema.Literal("image.edit"),
  source: Schema.Struct({
    assetId: Key,
    sha256: Schema.String.check(Schema.isPattern(/^[a-f0-9]{64}$/)),
  }),
});
export type CreateImageEditJob = typeof CreateImageEditJob.Type;
export const CreateNarrationJob = Schema.Struct({
  requestKey: Key,
  scope: Schema.Struct({ kind: Schema.Literal("local-draft") }),
  operation: Schema.Literal("audio.speak"),
  modelKey: Key,
  executionPolicy: Schema.Literal("local-only"),
  script: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(2000)),
  parameters: Schema.Struct({
    voiceId: Schema.Literal("bf_emma"),
    speed: Schema.Number.check(Schema.isBetween({ minimum: 0.5, maximum: 2 })),
  }),
});
export type CreateNarrationJob = typeof CreateNarrationJob.Type;
export const CreateSoundJob = Schema.Struct({
  requestKey: Key,
  scope: Schema.Struct({ kind: Schema.Literal("local-draft") }),
  operation: Schema.Literal("audio.generate"),
  modelKey: Key,
  executionPolicy: Schema.Literal("local-only"),
  prompt: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(2000)),
  parameters: Schema.Struct({
    durationSeconds: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 10 })),
    seed: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 2147483647 })),
    steps: Schema.Literal(25),
    guidance: Schema.Literal(3),
  }),
});
export type CreateSoundJob = typeof CreateSoundJob.Type;
/** Music uses its own controls; legacy Dasheng requests keep their exact shape. */
export const CreateMusicJob = Schema.Struct({
  requestKey: Key,
  scope: Schema.Struct({ kind: Schema.Literal("local-draft") }),
  operation: Schema.Literal("audio.generate"),
  audioKind: Schema.Literal("music"),
  modelKey: Key,
  executionPolicy: Schema.Literal("local-only"),
  prompt: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(2000)),
  parameters: Schema.Struct({
    durationSeconds: Schema.Int.check(Schema.isBetween({ minimum: 10, maximum: 120 })),
    bpm: Schema.Int.check(Schema.isBetween({ minimum: 30, maximum: 300 })),
    key: Schema.String.check(Schema.isPattern(/^[A-G](?:#|b)? (?:major|minor)$/)),
    seed: Schema.Int.check(Schema.isBetween({ minimum: 0, maximum: 2147483647 })),
    steps: Schema.Literal(8),
    instrumental: Schema.Literal(true),
  }),
});
export type CreateMusicJob = typeof CreateMusicJob.Type;
const VideoPreviewSettings = {
  frames: Schema.Literal(17),
  fps: Schema.Literal(16),
  seed: Schema.Int.check(Schema.isBetween({ minimum: 0, maximum: 2147483647 })),
  flowShift: Schema.Literal(3),
  quality: Schema.Literal("preview"),
};
const WanSampling = {
  steps: Schema.Literal(20),
  guidance: Schema.Literal(6),
  scheduler: Schema.optionalKey(Schema.Literal("discrete")),
};
export const CreateVideoJob = Schema.Struct({
  requestKey: Key,
  scope: Schema.Struct({ kind: Schema.Literal("local-draft") }),
  operation: Schema.Literal("video.generate"),
  modelKey: Key,
  executionPolicy: Schema.Literal("local-only"),
  prompt: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(2000)),
  parameters: Schema.Union([
    // Preserve exact retries of earlier small previews; new Designer takes
    // use the native engine's documented dimensions, qualified separately.
    Schema.Struct({
      width: Schema.Literal(512),
      height: Schema.Literal(320),
      ...VideoPreviewSettings,
      ...WanSampling,
    }),
    Schema.Struct({
      width: Schema.Literal(832),
      height: Schema.Literal(480),
      ...VideoPreviewSettings,
      ...WanSampling,
    }),
    Schema.Struct({
      width: Schema.Literal(832),
      height: Schema.Literal(480),
      ...VideoPreviewSettings,
      steps: Schema.Literal(3),
      frames: Schema.Literals([17, 49]),
      guidance: Schema.Literal(1),
      scheduler: Schema.Literal("lcm"),
    }),
  ]),
});
export type CreateVideoJob = typeof CreateVideoJob.Type;
export const CreateAnimationJob = Schema.Struct({
  ...CreateVideoJob.fields,
  operation: Schema.Literal("video.animate"),
  parameters: Schema.Struct({
    width: Dimension.check(Schema.isLessThanOrEqualTo(832)),
    height: Dimension.check(Schema.isLessThanOrEqualTo(832)),
    ...VideoPreviewSettings,
    ...WanSampling,
  }),
  source: Schema.Struct({
    assetId: Key,
    sha256: Schema.String.check(Schema.isPattern(/^[a-f0-9]{64}$/)),
  }),
});
export type CreateAnimationJob = typeof CreateAnimationJob.Type;
const AssetReference = Schema.Struct({
  assetId: Key,
  sha256: Schema.String.check(Schema.isPattern(/^[a-f0-9]{64}$/)),
});
export const CreateAssemblyJob = Schema.Struct({
  requestKey: Key,
  scope: Schema.Struct({ kind: Schema.Literal("local-draft") }),
  operation: Schema.Literal("media.assemble"),
  modelKey: Schema.Literal("local-cut"),
  executionPolicy: Schema.Literal("local-only"),
  prompt: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(2000)),
  parameters: Schema.Struct({
    width: Schema.Literals([480, 512, 832]),
    height: Schema.Literals([480, 512, 832]),
    fps: Schema.Literal(16),
    clips: Schema.Array(
      Schema.Struct({
        source: AssetReference,
        kind: Schema.Literals(["image", "video"]),
        frames: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 960 })),
      }),
    ).check(Schema.isMinLength(1), Schema.isMaxLength(8)),
    audio: Schema.Array(
      Schema.Struct({
        source: AssetReference,
        startFrame: Schema.Int.check(Schema.isBetween({ minimum: 0, maximum: 959 })),
        endFrame: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 960 })),
        gainDb: Schema.Number.check(Schema.isBetween({ minimum: -30, maximum: 6 })),
      }),
    ).check(Schema.isMaxLength(8)),
    captions: Schema.optional(
      Schema.Array(
        Schema.Struct({
          startFrame: Schema.Int.check(Schema.isBetween({ minimum: 0, maximum: 959 })),
          endFrame: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 960 })),
          text: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(500)),
        }),
      ).check(Schema.isMaxLength(60)),
    ),
    format: Schema.Literal("h264"),
  }),
});
export type CreateAssemblyJob = typeof CreateAssemblyJob.Type;
export const CreateMediaJob = Schema.Union([
  CreateImageJob,
  CreateImageEditJob,
  CreateNarrationJob,
  CreateSoundJob,
  CreateMusicJob,
  CreateVideoJob,
  CreateAnimationJob,
  CreateAssemblyJob,
]);
export type CreateMediaJob = typeof CreateMediaJob.Type;
export const MediaRequest = Schema.Union([
  Schema.Struct({
    action: Schema.Literal("models.remove"),
    params: Schema.Struct({ modelKey: Key }),
  }),
  Schema.Struct({
    action: Schema.Literal("models.list"),
    params: Schema.Struct({
      operation: Schema.optionalKey(MediaOperation),
      execution: Schema.optionalKey(Schema.Literal("local")),
    }),
  }),
  Schema.Struct({
    action: Schema.Literal("models.packs"),
    params: Schema.Struct({ operation: Schema.optionalKey(MediaOperation) }),
  }),
  Schema.Struct({
    action: Schema.Literal("models.prepare"),
    params: Schema.Struct({
      packKey: Key,
      requestKey: Key,
      sourceDirectory: Schema.optionalKey(Path),
    }),
  }),
  Schema.Struct({
    action: Schema.Literals(["models.preparation", "models.cancelPreparation"]),
    params: Schema.Struct({ preparationId: Key }),
  }),
  Schema.Struct({
    action: Schema.Literal("models.import"),
    params: Schema.Struct({
      kind: Schema.Literals([
        "z-image-sd-cpp",
        "dasheng-audiogen-cpp",
        "wan-sd-cpp",
        "wan-preview-sd-cpp",
        "fast-wan-preview-sd-cpp",
      ]),
      directory: Path,
      executable: Path,
    }),
  }),
  Schema.Struct({ action: Schema.Literal("jobs.create"), params: CreateMediaJob }),
  Schema.Struct({
    action: Schema.Literals(["jobs.read", "jobs.cancel"]),
    params: Schema.Struct({ jobId: Key }),
  }),
  Schema.Struct({ action: Schema.Literal("jobs.list"), params: Schema.Struct({}) }),
  Schema.Struct({ action: Schema.Literal("assets.read"), params: Schema.Struct({ assetId: Key }) }),
]);
export type MediaRequest = typeof MediaRequest.Type;
export interface MediaModelChoice {
  readonly modelKey: string;
  readonly name: string;
  readonly nativeModelId: string;
  readonly engineId: string;
  readonly execution: "local";
  readonly operations: readonly MediaOperation[];
  readonly availability: "ready" | "unavailable";
  readonly detail: string;
  readonly bytes: number;
  readonly modelDigest: string;
  readonly engineDigest: string;
  /** Explicit next-take defaults; an active request retains its own settings. */
  readonly videoPreview?: CreateVideoJob["parameters"];
  readonly videoFrameOptions?: readonly (17 | 49)[];
}
export interface MediaOutput {
  readonly assetId: string;
  readonly kind: "generation";
  readonly mimeType: "image/png" | "audio/wav" | "video/webm" | "video/mp4" | "text/vtt";
  readonly bytes: number;
  readonly sha256: string;
}
export interface MediaJobState {
  readonly jobId: string;
  readonly status: "queued" | "running" | "completed" | "failed" | "cancelled" | "interrupted";
  readonly stage: string;
  readonly progress: number;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly error: string | null;
  readonly producer: {
    readonly execution: "local";
    readonly modelDigest: string;
    readonly engineDigest: string;
    readonly engineId: string;
  };
  readonly outputs: readonly MediaOutput[];
  readonly executionMetrics?: {
    readonly elapsedSeconds: number;
    readonly initializationSeconds: number;
    readonly peakResidentBytes: number;
    readonly backend: string;
  };
}

export type MediaImageJob = (CreateImageJob | CreateImageEditJob) & MediaJobState;
export type MediaNarrationJob = CreateNarrationJob & MediaJobState;
export type MediaSoundJob = CreateSoundJob & MediaJobState;
export type MediaMusicJob = CreateMusicJob & MediaJobState;
export type MediaVideoJob = CreateVideoJob & MediaJobState;
export type MediaAssemblyJob = CreateAssemblyJob & MediaJobState;
export type MediaAnimationJob = CreateAnimationJob & MediaJobState;
export type MediaJob =
  | MediaImageJob
  | MediaNarrationJob
  | MediaSoundJob
  | MediaMusicJob
  | MediaVideoJob
  | MediaAnimationJob
  | MediaAssemblyJob;

export interface MediaPackPreparation {
  preparationId: string;
  packKey: string;
  requestKey: string;
  /** Binds exact preparation retries, including an optional local source folder. */
  requestDigest?: string;
  status: "running" | "completed" | "failed" | "cancelled" | "interrupted";
  stage: string;
  downloadedBytes: number;
  totalBytes: number;
  modelKey: string | null;
  files: readonly { name: string; sha256: string; bytes: number; verified: boolean }[];
  error: string | null;
  createdAt: string;
  updatedAt: string;
}
